/**
 * Kommer man till en annan sida mitt i ett annonsflöde ska vägen tillbaka
 * alltid vara ett klick bort. Destinationen sparas för sessionen och skickas
 * även med i adressen så att den överlever en omladdning.
 */
import { useEffect, useState, useSyncExternalStore } from "react";
import { useRouterState } from "@tanstack/react-router";

const KEY = "sellavo:retur";

export type ReturnTarget = { path: string; label: string };

/** Bara interna sökvägar får användas – aldrig en adress till en annan sajt. */
export function isInternalPath(value: string | undefined | null): value is string {
  return typeof value === "string" && /^\/[^/\\]/.test(value) && value.length < 300;
}

export function labelForPath(path: string): string {
  if (path.startsWith("/skapa")) return "Tillbaka till din annons";
  if (path.startsWith("/redigera")) return "Tillbaka till din redigering";
  return "Tillbaka dit du var";
}

export function rememberReturn(path: string): void {
  if (!isInternalPath(path)) return;
  try {
    window.sessionStorage.setItem(KEY, path);
  } catch {
    /* ignorera */
  }
}

export function readReturn(): string | null {
  try {
    const value = window.sessionStorage.getItem(KEY);
    return isInternalPath(value) ? value : null;
  } catch {
    return null;
  }
}

export function clearReturn(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* ignorera */
  }
}

/** Retur-destinationen för den här sidan: adressen först, annars sessionen. */
export function useReturnTo(fromSearch?: string): ReturnTarget | null {
  const [target, setTarget] = useState<ReturnTarget | null>(null);

  useEffect(() => {
    const path = isInternalPath(fromSearch) ? fromSearch : readReturn();
    if (!path) {
      setTarget(null);
      return;
    }
    rememberReturn(path);
    setTarget({ path, label: labelForPath(path) });
  }, [fromSearch]);

  return target;
}

/* --------------------------------------------------------------------------
 * Spårning av föregående sida, så att en tillbaka-länk kan anpassa sig efter
 * varifrån besökaren faktiskt kom.
 * ------------------------------------------------------------------------ */

const PREV_KEY = "sellavo:forra";
const CURRENT_KEY = "sellavo:nuvarande";

export type BackTarget = {
  path: string;
  search: Record<string, string>;
  label: string;
  /** Stabil nyckel så att tillbaka-länken kan översättas. */
  key: string;
};


let prevHref: string | null = null;
let currentHref: string | null = null;
const listeners = new Set<() => void>();

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function hydrateFromSession() {
  if (currentHref !== null) return;
  try {
    prevHref = window.sessionStorage.getItem(PREV_KEY);
    currentHref = window.sessionStorage.getItem(CURRENT_KEY);
  } catch {
    /* ignorera */
  }
}

/** Sparar föregående sida varje gång man navigerar internt. */
export function usePreviousPathTracker(href: string): void {
  useEffect(() => {
    hydrateFromSession();
    if (currentHref && currentHref !== href) prevHref = currentHref;
    currentHref = href;
    try {
      if (prevHref) window.sessionStorage.setItem(PREV_KEY, prevHref);
      window.sessionStorage.setItem(CURRENT_KEY, href);
    } catch {
      /* ignorera */
    }
    for (const fn of listeners) fn();
  }, [href]);
}

const HOME: BackTarget = { path: "/", search: {}, label: "Tillbaka till startsidan", key: "home" };

/** Kända sidor och vad tillbaka-länken ska heta när man kom därifrån. */
const KNOWN: { prefix: string; label: string }[] = [
  { prefix: "/mina-annonser", label: "Tillbaka till Mina annonser" },
  { prefix: "/mina-prylar", label: "Tillbaka till Mina prylar" },
  { prefix: "/mina-affarer", label: "Tillbaka till Mina affärer" },
  { prefix: "/mina-arenden", label: "Tillbaka till Mina ärenden" },
  { prefix: "/mina-bud", label: "Tillbaka till Mina bud" },
  { prefix: "/meddelanden", label: "Tillbaka till Meddelanden" },
  { prefix: "/oversikt", label: "Tillbaka till Översikt" },
  { prefix: "/statistik", label: "Tillbaka till Statistik" },
  { prefix: "/adresser", label: "Tillbaka till Adresser" },
  { prefix: "/profil", label: "Tillbaka till Min profil" },
  { prefix: "/konto", label: "Tillbaka till Mitt konto" },
  { prefix: "/min-niva", label: "Tillbaka till Min nivå" },
  { prefix: "/priser", label: "Tillbaka till Priser" },
  { prefix: "/annonser", label: "Tillbaka till annonserna" },
  { prefix: "/videoskanning", label: "Tillbaka till videoskanning" },
  { prefix: "/support", label: "Tillbaka till supporten" },
  { prefix: "/lyft", label: "Tillbaka till Lyft annonsen" },
  { prefix: "/order", label: "Tillbaka till ordern" },
  { prefix: "/admin", label: "Tillbaka till administrationen" },
  { prefix: "/regler", label: "Tillbaka till reglerna" },
  { prefix: "/om", label: "Tillbaka till Om Sellavo" },
  { prefix: "/sa-fungerar-det", label: "Tillbaka till Så fungerar det" },
  { prefix: "/tips", label: "Tillbaka till tipsen" },
];

/** Vilken tillbaka-destination hör ihop med den sida man kom ifrån? */
export function backTargetFor(href: string | null): BackTarget {
  if (!isInternalPath(href)) return HOME;
  const [rawPath, rawSearch = ""] = href.split("?");
  const path = rawPath ?? "/";
  const search: Record<string, string> = {};
  for (const [k, v] of new URLSearchParams(rawSearch).entries()) search[k] = v;

  if (path === "/") return HOME;
  if (path.startsWith("/a/"))
    return { path, search: {}, label: "Tillbaka till annonsen", key: "ad" };
  if (path.startsWith("/skapa"))
    return { path: "/skapa", search, label: "Tillbaka till din annons", key: "create" };
  if (path.startsWith("/redigera"))
    return { path, search, label: "Tillbaka till din redigering", key: "edit" };

  const known = KNOWN.find((k) => path === k.prefix || path.startsWith(`${k.prefix}/`));
  if (known) return { path, search, label: known.label, key: known.prefix };

  // Okänd intern sida: gå tillbaka dit i stället för att kasta ut till startsidan.
  return { path, search, label: "Tillbaka", key: "back" };
}


/** Tillbaka-destination baserat på föregående sida (först efter hydrering). */
export function useBackTarget(): BackTarget {
  const here = useRouterState({ select: (r) => r.location.href });
  const prev = useSyncExternalStore(
    subscribe,
    () => {
      hydrateFromSession();
      return prevHref;
    },
    () => null,
  );
  const samePage = prev ? (prev.split("?")[0] ?? "") === (here.split("?")[0] ?? "") : true;
  return backTargetFor(prev && !samePage ? prev : null);
}
